export const NAV_LINKS = [
  { label: "About", href: "#about" },
  { label: "Selected Work", href: "#work" },
  { label: "Skills", href: "#skills" },
  { label: "Education", href: "#education" },
  { label: "Contact", href: "#contact" },
];

export const CV_URL = "/files/Irene-Chen-CV.pdf";
export const DISSERTATION_URL = "/files/Irene-Chen-Dissertation-Lay-Summary.pdf";

export const CONTACT = {
  name: "Hsiang-Chun (Irene) Chen",
  email: "irenehcc090@gmail.com",
  linkedin: "https://www.linkedin.com/in/irene-chen-0276a0346",
  linkedinLabel: "linkedin.com/in/irene-chen-0276a0346",
  phone: "+44 7344 124593",
  location: "Exeter, UK — open to relocation",
};

export const MARQUEE_ITEMS = [
  "Strategic Problem Solving",
  "MSc Psychology — University of Exeter",
  "Cross-Functional Team Leadership",
  "Qualitative & Quantitative Analysis",
  "English · Mandarin · Korean · German · Taiwanese",
  "Crisis Management & Event Execution",
  "Business Process Structuring",
];

export const CASE_STUDIES = [
  {
    id: "orientation-programme",
    number: "01",
    tag: "Event Logistics & Crisis Management",
    title: "Inter-Organisation Orientation Programme",
    date: "2019",
    summary:
      "Kept an 800+ participant programme on schedule after the venue fell through days before the event — securing a replacement overnight and reconfirming with all 40 partner schools before the deadline.",
    metrics: [
      { value: "800+", label: "Participants" },
      { value: "40", label: "Partner schools" },
      { value: "100%", label: "On-time submissions" },
      { value: "Overnight", label: "Venue replacement" },
    ],
    context:
      "A cross-school orientation programme for 800+ participants, coordinated with 40 partner schools across central Taiwan. As Activities Team Lead I was the sole approver for submissions and the single point of accountability for keeping the event on track.",
    problem:
      "Coordinating with 40 partner schools meant constant back-and-forth: audio files arrived with technical issues and needed revising, deadlines kept moving, and dance validation sessions had to be rescheduled. Then, days before the event, the confirmed venue fell through — with every detail already committed.",
    actions: [
      "Assessed replacement options against capacity, stage requirements and hire costs within hours of the cancellation.",
      "Secured a suitable replacement venue overnight, before the news could unsettle the wider team.",
      "Reconfirmed logistics individually with all 40 partner schools ahead of the deadline, so no participant arrived at the wrong place.",
      "Beyond the crisis, handled the role's core work: wrote the programme proposal and acted as the day-to-day contact for all 40 schools — fielding audio revisions, deadline changes and questions throughout the preparation period.",
    ],
    takeaways:
      "A crisis handled quietly is a crisis halved: the faster the coordinator absorbs the shock, decides and communicates, the less the team and the guests ever feel it.",
    placeholder:
      "To add: the venue comparison notes, or a reference from a partner school.",
    image: "/images/orientation-2019.jpg",
    imageAlt: "Inter-Organisation Orientation Programme 2019 group photo in a school gymnasium",
  },
  {
    id: "epoch-showcase",
    number: "02",
    tag: "Sponsorship & Multi-Team Leadership",
    title: "EPOCH Dance Showcase",
    date: "2020",
    summary:
      "As Chief Coordinator, helped raise NT$150,000 (~£3,750) in sponsorship and led six sub-teams — 22 members and 40–50 performers from three schools — through shifting priorities to deliver the show.",
    metrics: [
      { value: "NT$150k", label: "Sponsorship helped raise" },
      { value: "22", label: "Team members" },
      { value: "6", label: "Sub-teams led" },
      { value: "3", label: "Schools united" },
    ],
    context:
      "EPOCH was a dance showcase uniting 40–50 performers across three schools. As Chief Coordinator I led six sub-teams of 22 members — and also helped fund the production.",
    problem:
      "A show of this scale needed real money, and the plan kept shifting: priorities changed mid-stream, and every change risked pulling the six teams in different directions.",
    actions: [
      "Assisted with sponsorship proposals and supplier negotiations, helping raise NT$150,000 (~£3,750).",
      "Led six sub-teams — 22 members across three schools — through shifting priorities while keeping one shared plan.",
      "Introduced a pre-decision objection round: before any major call was finalised, the team had a structured chance to surface problems — catching issues before execution instead of after.",
      "Learned to balance warmth and firmness as a coordinator — staying approachable and getting along with everyone day to day, while knowing when to push, chase and be serious.",
    ],
    takeaways:
      "Alignment is built, not assumed: giving people a formal moment to object before a decision turns potential conflict into better plans — and keeps six teams moving as one.",
    placeholder:
      "To add: the sponsorship proposal outline, or sponsor names you are happy to list.",
    image: "/images/epoch-stage.webp",
    imageAlt: "EPOCH dance showcase performers and organisers on stage",
  },
  {
    id: "epoch-campaign",
    number: "03",
    tag: "Fundraising Strategy & Operations",
    title: "EPOCH Badge Pre-order Campaign",
    date: "2020",
    summary:
      "A story within EPOCH: proposed and launched a limited-edition badge pre-order campaign that closed the showcase's NT$7,500 funding gap — selling 318+ badges for ~NT$10,000 net, a 33% surplus.",
    metrics: [
      { value: "NT$7,500", label: "Funding gap closed" },
      { value: "318+", label: "Badges sold" },
      { value: "~NT$10k", label: "Net profit" },
      { value: "+33%", label: "Surplus over the gap" },
    ],
    context:
      "Even after EPOCH's sponsorship came in, the production still faced a NT$7,500 funding gap. With no budget to absorb losses, the answer had to generate revenue without any financial risk.",
    problem:
      "With uncertain demand and zero spare capital, a conventional 'print first, sell later' approach risked unsold stock eating into whatever the campaign earned.",
    actions: [
      "Proposed a pre-order model for limited-edition badges so production quantities exactly matched paid demand, removing inventory risk entirely.",
      "Worked through the unit economics — supplier quotes, pricing and order thresholds — to set a price point affordable for students and still profitable.",
      "Coordinated supplier timelines and checked sample quality before committing to the bulk run.",
      "Managed order collection and tracking so every pre-order was matched, paid and fulfilled accurately — 318+ badges, ~NT$10,000 net.",
    ],
    takeaways:
      "Small projects teach the same discipline as large ones: know your numbers before you commit, de-risk the decision, and communicate clearly with the people paying you.",
    placeholder:
      "To add: the campaign's costing model, badge designs, and a short note on how the surplus was used.",
    image: "/images/epoch-promo.webp",
    imageAlt: "EPOCH dance showcase promotional group photo",
  },
  {
    id: "fju-showcase",
    number: "04",
    tag: "Budget Ownership & Live Hosting",
    title: "FJU German Department Graduation Showcase",
    date: "Mar – May 2025",
    summary:
      "As Chief Coordinator & Host: secured a NT$30,000 departmental budget, coordinated 3 judges, 5 contestants, university staff and a 5-person team — then ran the live programme to time as host.",
    metrics: [
      { value: "NT$30k", label: "Budget secured" },
      { value: "25+", label: "Attendees" },
      { value: "5", label: "Team members" },
      { value: "On time", label: "Live programme" },
    ],
    context:
      "The German Department's graduation showcase at Fu Jen Catholic University. I served as both Chief Coordinator and Host — owning the budget, the team and the live run of the evening.",
    problem:
      "The event existed only if funding did — and on the night itself, everything I had coordinated had to land in real time, in front of the audience, with no second takes.",
    actions: [
      "Wrote the funding proposal that secured a NT$30,000 departmental budget, then allocated it across catering and prizes for a 25+ attendee event.",
      "Acted as the single point of contact between 3 judges, 5 contestants, university staff and a 5-person team — assigning individual targets and tracking progress.",
      "Ran the live programme as host: directing every transition and timing so the evening kept to schedule.",
    ],
    takeaways:
      "Coordination and performance are different muscles: the same event taught me to plan quietly for months — and to hold a room's attention calmly for hours.",
    placeholder:
      "To add: photos from the evening, the run-of-show, or feedback from the department.",
    image:
      "https://images.unsplash.com/photo-1548248823-ce16a73b6d49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMG1pbmltYWxpc3QlMjB3YXJtJTIwYXJjaGl0ZWN0dXJhbCUyMHRleHR1cmUlMjBnZW9tZXRyeXxlbnwwfHx8fDE3OTM3MjMwNzJ8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "chenlux",
    number: "05",
    tag: "B2B Marketing & Supplier Coordination",
    title: "Chenlux Technology — Automation Taipei 2023",
    date: "Jul – Aug 2023",
    summary:
      "Marketing Assistant at an industrial automation trade show: briefed 10+ booth visitors, consolidated post-exhibition sales data into a single follow-up source, and delivered all materials plus 30+ sample shipments before deadline.",
    metrics: [
      { value: "10+", label: "Visitor briefings" },
      { value: "30+", label: "Sample shipments" },
      { value: "1", label: "Unified follow-up source" },
      { value: "100%", label: "On-time delivery" },
    ],
    context:
      "A summer placement as Marketing Assistant at Chenlux Technology in Taichung, centred on the company's presence at Automation Taipei 2023 — Taiwan's major industrial automation exhibition.",
    problem:
      "Trade shows generate a flood of loose ends: visitor conversations, customer records and sample requests all living in different places, and every one of them going stale within days if nobody owns the follow-up.",
    actions: [
      "Prepared catalogues and presentations and staffed the company booth, briefing 10+ visitors on product applications.",
      "Consolidated customer records, event responses and sales data into a single source the sales team could use for post-exhibition follow-up.",
      "Coordinated directly with suppliers to deliver all exhibition materials and 30+ sample shipments before deadline.",
    ],
    takeaways:
      "My first taste of commercial operations: an exhibition is a project with a hard deadline, and its real value is decided in the follow-up — not on the show floor.",
    placeholder:
      "To add: the exhibition name badge, booth photos, or a note on what the follow-up data enabled.",
    image: "/images/chenlux-expo.png",
    imageAlt: "Automation Taipei trade show floor with exhibitor booths",
  },
];

export const DISSERTATION = {
  id: "dissertation",
  number: "—",
  tag: "Research Design & Mixed-Methods Analysis",
  title: "MSc Dissertation: Academic Stress & Memory Retrieval",
  summary:
    "A mixed-methods empirical study at the University of Exeter exploring how academic stress relates to memory retrieval, combining psychometric surveys with semi-structured interviews.",
  context:
    "My MSc dissertation at the University of Exeter investigated how academic stress may influence memory retrieval — a question that sits at the intersection of cognitive psychology and the everyday reality of student life.",
  problem:
    "Stress and memory are both hard to pin down: surveys capture measurable trends but miss lived experience, while interviews capture depth but not patterns. The study needed both, honestly combined.",
  actions: [
    "Designed the study protocol and ethics submission, recruited participants and collected psychometric survey responses.",
    "Conducted semi-structured interviews and thematically coded the transcripts.",
    "Integrated the survey data with the interview material into an explanatory model linking academic stress, sleep and self-expectations — six themes in total.",
  ],
  caveat:
    "An honest caveat: with a small sample, findings are exploratory rather than generalisable — the study's value lies in its methodological triangulation and the questions it opens, not population-level claims.",
  takeaways:
    "Working honestly with imperfect data: stating what the evidence can and cannot support is itself an analytical skill — one that matters as much in advisory work as in research.",
  placeholder:
    "To add: dissertation supervisor name. The full dissertation text can be linked here once you are ready to share it.",
  image:
    "https://images.unsplash.com/photo-1532680678473-a16f2cda8e43?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMG1pbmltYWxpc3QlMjB3YXJtJTIwYXJjaGl0ZWN0dXJhbCUyMHRleHR1cmUlMjBnZW9tZXRyeXxlbnwwfHx8fDE3OTM3MjMwNzJ8MA&ixlib=rb-4.1.0&q=85",
  download: {
    href: "/files/Irene-Chen-Dissertation-Lay-Summary.pdf",
    label: "Download Lay Summary (PDF)",
  },
};

export const SKILLS = [
  {
    id: "consulting",
    title: "Consulting & Problem Solving",
    items: [
      "Structuring ambiguous problems",
      "Qualitative & quantitative synthesis",
      "Stakeholder communication",
      "Clear written reporting",
    ],
  },
  {
    id: "execution",
    title: "Project & Team Execution",
    items: [
      "Cross-functional coordination",
      "Crisis management",
      "Event & logistics planning",
      "Supplier & vendor handling",
    ],
  },
  {
    id: "research",
    title: "Research, Analysis & Tools",
    items: [
      "Survey & experimental design (Qualtrics)",
      "Thematic interview coding",
      "Statistics in Jamovi (t-tests, ANOVA, regression)",
      "Excel · PowerPoint · Power BI",
    ],
  },
  {
    id: "languages",
    title: "Languages & Culture",
    items: [
      "Mandarin & Taiwanese — native",
      "English — IELTS 7.5",
      "Korean — TOPIK 4",
      "German — intermediate",
      "Japanese — basic",
    ],
  },
];

export const EDUCATION = [
  {
    id: "exeter",
    institution: "University of Exeter",
    degree: "MSc Psychology (Conversion)",
    date: "Sep 2025 – Nov 2026",
    location: "Exeter, United Kingdom",
    note: "2:1 (Merit); statistics 93% (t-tests, ANOVA, correlation, regression in Jamovi). Dissertation: an explanatory model of academic stress and memory retrieval, integrating survey data with semi-structured interviews analysed into six themes.",
  },
  {
    id: "fujen",
    institution: "Fu Jen Catholic University",
    degree: "BA German Language and Culture",
    date: "2021 – 2025",
    location: "Taiwan",
    note: "GPA 3.3/4.0. Intercultural communication, literary analysis and linguistic structure — the foundation for a later turn towards psychology.",
  },
  {
    id: "marburg",
    institution: "Philipps-Universität Marburg",
    degree: "Year-Long Academic Exchange",
    date: "2024",
    location: "Marburg, Germany",
    note: "Full immersion in German academic life; coursework and everyday collaboration entirely in German.",
  },
];
